import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import NotifyIcon from './common/NotifyIcon';
import {
    getDeliveryNotificationConfig,
    buildDeliveryNotificationMessage,
    resolveNotificationOrderNo,
} from '../utils/deliveryNotificationText';
import './DeliveryNotificationSlide.css';

const AUTO_CLOSE_MS = 5000;

function NotificationCard({ notification, onDismiss }) {
    const [remaining, setRemaining] = useState(AUTO_CLOSE_MS);
    const [closing, setClosing] = useState(false);
    const startRef = useRef(Date.now());
    const pausedAtRef = useRef(AUTO_CLOSE_MS);
    const closingRef = useRef(false);
    const timerRef = useRef(null);

    const config = getDeliveryNotificationConfig(notification);
    const message = buildDeliveryNotificationMessage(notification);
    const orderNo = resolveNotificationOrderNo(notification);
    const progress = (remaining / AUTO_CLOSE_MS) * 100;

    const stopTimer = useCallback(() => {
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }, []);

    const doClose = useCallback(() => {
        if (closingRef.current) return;
        closingRef.current = true;
        stopTimer();
        setClosing(true);
        setTimeout(() => onDismiss(notification.id), 400);
    }, [notification.id, onDismiss, stopTimer]);

    const tick = useCallback(() => {
        if (closingRef.current) return;
        const left = Math.max(AUTO_CLOSE_MS - (Date.now() - startRef.current), 0);
        setRemaining(left);
        if (left <= 0) doClose();
    }, [doClose]);

    const startTimer = useCallback(() => {
        stopTimer();
        timerRef.current = setInterval(tick, 100);
    }, [stopTimer, tick]);

    useEffect(() => {
        startRef.current = Date.now();
        startTimer();
        return stopTimer;
    }, [startTimer, stopTimer]);

    const handlePause = useCallback(() => {
        if (closingRef.current) return;
        pausedAtRef.current = remaining;
        stopTimer();
    }, [remaining, stopTimer]);

    const handleResume = useCallback(() => {
        if (closingRef.current) return;
        startRef.current = Date.now() - (AUTO_CLOSE_MS - pausedAtRef.current);
        startTimer();
    }, [startTimer]);

    return (
        <div
            className={`dn-card ${closing ? 'dn-closing' : ''}`}
            onMouseEnter={handlePause}
            onMouseLeave={handleResume}
            onTouchStart={handlePause}
            onTouchEnd={handleResume}
        >
            <div className="dn-progress">
                <div className="dn-progress-bar" style={{ width: `${progress}%`, background: config.color }} />
            </div>
            <div className="dn-header">
                <span className="dn-icon" style={{ color: config.color }}><NotifyIcon name={config.icon} size={20} /></span>
                <div className="dn-title-wrap">
                    <h4 className="dn-title">{config.title}</h4>
                    {notification.partnerName && <span className="dn-subtitle">{notification.partnerName}</span>}
                </div>
                <button className="dn-close-btn" onClick={doClose}><X size={14} /></button>
            </div>
            <div className="dn-body">
                <p className="dn-message">{message}</p>
                <div className="dn-details">
                    {orderNo && (
                        <div className="dn-detail-row">
                            <span>Sipariş</span>
                            <strong>#{orderNo}</strong>
                        </div>
                    )}
                    {notification.courierName && (
                        <div className="dn-detail-row">
                            <span>Kurye</span>
                            <strong>{notification.courierName}</strong>
                        </div>
                    )}
                    {notification.regionName && (
                        <div className="dn-detail-row">
                            <span>Bölge</span>
                            <strong>{notification.regionName}</strong>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function DeliveryNotificationSlide({ notifications, onDismiss, onDismissAll }) {
    if (!notifications.length) return null;

    return (
        <div className="dn-container">
            {notifications.length >= 2 && (
                <button className="dn-dismiss-all" onClick={onDismissAll}>
                    <X size={14} />
                    Tümünü Kapat ({notifications.length})
                </button>
            )}
            <div className="dn-stack">
                {notifications.map(n => (
                    <NotificationCard key={n.id} notification={n} onDismiss={onDismiss} />
                ))}
            </div>
        </div>
    );
}
